/**
 * Value coercion for Firestore documents.
 *
 * Timestamps are normalized to epoch milliseconds and numbers to integers at
 * the boundary, so the rest of the domain never has to know about Firestore
 * `Timestamp` objects or about strings arriving where a number is expected.
 */

export type Raw = Record<string, unknown>;

export function asRecord(value: unknown): Raw {
  return value && typeof value === 'object' ? value as Raw : {};
}

/** Accept epoch ms, ISO strings and Firestore Timestamp-like objects. */
export function toMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const obj = asRecord(value);
  if (typeof obj.toMillis === 'function') {
    const millis = (obj.toMillis as () => unknown)();
    return typeof millis === 'number' ? millis : null;
  }
  const seconds = obj.seconds ?? obj._seconds;
  if (typeof seconds === 'number') return seconds * 1000;
  return null;
}

export function asInt(value: unknown, fallback = 0): number {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : fallback;
}

export function asNullableInt(value: unknown): number | null {
  if (value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : null;
}

export function asText(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text ? text : null;
}
